import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Loader2, Sparkles, CheckCircle2,
  AlertTriangle, RotateCcw, Beaker,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { adminApi } from "../lib/adminAuth";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ADMIN_TOKEN_KEY = "medskin_admin_token";

const VERDICT_STYLE = {
  LOW:      "bg-emerald-50 text-emerald-800 ring-emerald-200",
  MODERATE: "bg-amber-50 text-amber-800 ring-amber-200",
  HIGH:     "bg-rose-50 text-rose-800 ring-rose-200",
};
const VERDICT_LABEL = { LOW: "No label conflict found", MODERATE: "Caution", HIGH: "Avoid" };

function StatTile({ label, value, tone = "slate", testid }) {
  const TONES = {
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border p-5 ${TONES[tone]}`}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wider opacity-80">{label}</p>
      <p className="mt-2 text-3xl font-semibold">{value}</p>
    </div>
  );
}

function HandWrittenEditor() {
  const [slugA, setSlugA] = useState("");
  const [slugB, setSlugB] = useState("");
  const [fields, setFields] = useState({ hand_written_content: "", author_name: "Meghana", source_url: "", status: "draft" });
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!slugA.trim() || !slugB.trim()) { toast.error("Enter both ingredient slugs"); return; }
    setBusy(true);
    try {
      const { data } = await adminApi().get(`/admin/v2/pair-content/one/${slugA.trim()}/${slugB.trim()}`);
      setFields({
        hand_written_content: data.hand_written_content || "",
        author_name: data.author_name || "Meghana",
        source_url: data.source_url || "",
        status: data.status || "draft",
      });
      setLoaded(true);
      toast.success(`Loaded ${data.pair_key}`);
    } catch { toast.error("Failed to load pair"); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await adminApi().put(`/admin/v2/pair-content/edit/${slugA.trim()}/${slugB.trim()}`, fields);
      toast.success(`Saved (${fields.status})`);
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="pair-handwritten-editor" className="mt-8 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
      <h2 className="text-lg font-semibold text-stone-900">Hand-written content editor</h2>
      <p className="mt-1 text-sm text-stone-600">Author byline + draft/published. Published + non-empty content renders above the auto text on the pair page.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <input data-testid="hw-slug-a" value={slugA} onChange={(e) => setSlugA(e.target.value)} placeholder="ingredient a slug (e.g. retinol)" className="h-10 rounded-lg border border-stone-300 px-3 text-sm" />
        <input data-testid="hw-slug-b" value={slugB} onChange={(e) => setSlugB(e.target.value)} placeholder="ingredient b slug (e.g. niacinamide)" className="h-10 rounded-lg border border-stone-300 px-3 text-sm" />
        <Button data-testid="hw-load" onClick={load} disabled={busy} variant="outline">Load pair</Button>
      </div>
      {loaded && (
        <div className="mt-4 space-y-3">
          <textarea data-testid="hw-content" value={fields.hand_written_content} onChange={(e) => setFields((f) => ({ ...f, hand_written_content: e.target.value }))} rows={8} placeholder="Hand-written markdown (leave empty to keep the auto page)" className="w-full rounded-lg border border-stone-300 p-3 font-mono text-[13px]" />
          <div className="flex flex-wrap gap-2">
            <input data-testid="hw-author" value={fields.author_name} onChange={(e) => setFields((f) => ({ ...f, author_name: e.target.value }))} placeholder="Author name" className="h-10 rounded-lg border border-stone-300 px-3 text-sm" />
            <input data-testid="hw-source" value={fields.source_url} onChange={(e) => setFields((f) => ({ ...f, source_url: e.target.value }))} placeholder="Source URL" className="h-10 flex-1 rounded-lg border border-stone-300 px-3 text-sm" />
            <select data-testid="hw-status" value={fields.status} onChange={(e) => setFields((f) => ({ ...f, status: e.target.value }))} className="h-10 rounded-lg border border-stone-300 px-3 text-sm">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
            <Button data-testid="hw-save" onClick={save} disabled={busy}>Save</Button>
          </div>
        </div>
      )}
    </section>
  );
}


export default function AdminPairContent() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyKey, setBusyKey] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [progress, setProgress] = useState({ i: 0, total: 0, done: 0 });
  const [log, setLog] = useState([]);
  const abortRef = useRef(null);

  const loadCandidates = useCallback(async () => {
    try {
      const { data } = await adminApi().get("/admin/v2/pair-content/candidates");
      setCandidates(data || []);
      setError("");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(msg);
      toast.error(`Failed to load candidates: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCandidates(); }, [loadCandidates]);

  const stats = useMemo(() => {
    const total = candidates.length;
    const inDb = candidates.filter((p) => p.in_db).length;
    const inHard = candidates.filter((p) => p.in_hardcoded).length;
    return {
      total,
      generated: inDb,
      hardcoded: inHard,
      remaining: total - inDb,
    };
  }, [candidates]);

  async function onGenerateOne(pair) {
    if (busyKey) return;
    setBusyKey(pair.pair_key);
    try {
      const { data } = await adminApi().post(
        `/admin/v2/pair-content/generate/${pair.slug_a}/${pair.slug_b}`,
      );
      toast.success(
        `Generated ${data.name_a} + ${data.name_b}`,
        { description: `Verdict: ${VERDICT_LABEL[data.verdict]}` },
      );
      await loadCandidates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Generation failed");
    } finally {
      setBusyKey(null);
    }
  }

  async function onRollbackOne(pair) {
    if (busyKey) return;
    if (!window.confirm(`Roll back ${pair.name_a} + ${pair.name_b}? This deletes the AI-generated content.`)) return;
    setBusyKey(pair.pair_key);
    try {
      await adminApi().delete(
        `/admin/v2/pair-content/${pair.slug_a}/${pair.slug_b}`,
      );
      toast.success(`Rolled back ${pair.name_a} + ${pair.name_b}`);
      await loadCandidates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Rollback failed");
    } finally {
      setBusyKey(null);
    }
  }

  async function onGenerateAll() {
    if (bulkBusy) return;
    setBulkBusy(true);
    setLog([]);
    setProgress({ i: 0, total: 0, done: 0 });
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const token = localStorage.getItem(ADMIN_TOKEN_KEY);
      const resp = await fetch(
        `${BACKEND}/api/admin/v2/pair-content/generate-all`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        },
      );
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const evt = JSON.parse(line);
            if (evt.event === "start") {
              setProgress({ i: 0, total: evt.total, done: 0 });
            } else if (evt.event === "ok") {
              setProgress((p) => ({ i: evt.index, total: evt.total, done: p.done + 1 }));
              setLog((l) => [{
                ok: true,
                title: `${evt.name_a} + ${evt.name_b}`,
                verdict: evt.verdict,
                preview: evt.why_preview,
              }, ...l].slice(0, 60));
            } else if (evt.event === "error") {
              setProgress((p) => ({ ...p, i: evt.index }));
              setLog((l) => [{
                ok: false,
                title: evt.pair_key,
                detail: evt.detail,
              }, ...l].slice(0, 60));
            } else if (evt.event === "done") {
              toast.success(`Done — ${evt.total} pair${evt.total === 1 ? "" : "s"} generated`);
            }
          } catch { /* swallow malformed lines */ }
        }
      }
      await loadCandidates();
    } catch (e) {
      if (e.name !== "AbortError")
        toast.error(e?.message || "Bulk generate failed");
    } finally {
      setBulkBusy(false);
      abortRef.current = null;
    }
  }

  return (
    <main
      data-testid="admin-pair-content-page"
      className="min-h-screen bg-[#fdf8f3] px-6 py-10"
    >
      <div className="mx-auto max-w-5xl">
        <AdminHeader />

        <h1 className="text-3xl font-semibold tracking-tight text-stone-900">
          Pair Content Generator
        </h1>
        <p className="mt-1 text-sm text-stone-600">
          Auto-fill expert ingredient-pair copy for the top {stats.total} highest-search-volume
          combinations using Claude Sonnet 4.5. Generated copy goes live instantly
          on every <code className="rounded bg-stone-100 px-1.5 py-0.5 font-mono text-[11px]">/ingredients/&#123;a&#125;-and-&#123;b&#125;</code> SEO page.
        </p>

        <HandWrittenEditor />


        <section className="mt-8 grid gap-4 md:grid-cols-4">
          <StatTile testid="stat-total" tone="slate" label="Top candidates" value={stats.total} />
          <StatTile testid="stat-generated" tone="emerald" label="AI-generated" value={stats.generated} />
          <StatTile testid="stat-hardcoded" tone="slate" label="Hardcoded curated" value={stats.hardcoded} />
          <StatTile testid="stat-remaining" tone="amber" label="Pending" value={stats.remaining} />
        </section>

        <section className="mt-8 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-stone-900">Bulk-generate every pending pair</p>
              <p className="mt-0.5 text-xs text-stone-600">
                Runs Claude sequentially with 1.2s pacing (keeps under the 50 RPM rate limit).
                Stream resumes on page-refresh of incomplete runs — already-generated pairs are skipped.
              </p>
            </div>
            <Button
              type="button"
              onClick={onGenerateAll}
              disabled={bulkBusy || stats.remaining === 0}
              data-testid="generate-all-button"
              className="inline-flex items-center gap-1.5"
            >
              {bulkBusy
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Sparkles className="h-4 w-4" />}
              {bulkBusy ? "Generating…" : `Generate ${stats.remaining} pending`}
            </Button>
          </div>

          {bulkBusy && (
            <div className="mt-4" data-testid="bulk-progress">
              <div className="flex items-center justify-between text-xs text-stone-600">
                <span>{progress.i} / {progress.total}</span>
                <span>{progress.done} succeeded</span>
              </div>
              <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-stone-100">
                <div
                  className="h-full bg-emerald-500 transition-all"
                  style={{ width: `${progress.total ? (progress.i / progress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}

          {log.length > 0 && (
            <div data-testid="activity-log" className="mt-4 max-h-64 overflow-y-auto rounded-lg border border-stone-200 bg-stone-50/50 p-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                Activity log
              </p>
              <ul className="space-y-1.5">
                {log.map((entry, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-xs">
                    {entry.ok ? (
                      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    ) : (
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-600" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-stone-900">{entry.title}</p>
                      {entry.ok && (
                        <p className="text-stone-600">
                          <span className={`mr-1.5 inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold ring-1 ${VERDICT_STYLE[entry.verdict]}`}>
                            {VERDICT_LABEL[entry.verdict]}
                          </span>
                          {entry.preview}…
                        </p>
                      )}
                      {!entry.ok && <p className="text-rose-700">{entry.detail}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {error && (
          <p data-testid="pair-content-error" className="mt-6 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}

        {loading ? (
          <div className="mt-8 flex items-center gap-2 text-stone-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading candidates…
          </div>
        ) : (
          <section data-testid="pair-candidates-table" className="mt-8 rounded-2xl border border-stone-200 bg-white shadow-sm">
            <div className="border-b border-stone-200 p-4">
              <p className="text-sm font-semibold text-stone-900">Top 50 pairs (ranked by product penetration)</p>
              <p className="mt-0.5 text-xs text-stone-600">
                Pairs already in the hardcoded curated dict are marked "Hardcoded".
                Pairs authored by Claude are marked "AI-generated" — these take precedence at SSR time.
              </p>
            </div>
            <ul className="divide-y divide-stone-100">
              {candidates.map((pair) => {
                const status = pair.in_db
                  ? "ai"
                  : pair.in_hardcoded
                    ? "hardcoded"
                    : "pending";
                return (
                  <li
                    key={pair.pair_key}
                    data-testid={`pair-row-${pair.pair_key}`}
                    className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 hover:bg-stone-50/40"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Beaker className="h-3.5 w-3.5 shrink-0 text-stone-400" />
                        <span className="font-semibold text-stone-900">{pair.name_a}</span>
                        <span className="text-stone-400">+</span>
                        <span className="font-semibold text-stone-900">{pair.name_b}</span>
                      </div>
                      <p className="mt-0.5 text-[11px] text-stone-500">
                        Penetration score: {pair.penetration_score}
                        {pair.verdict && (
                          <>
                            {" · "}
                            <span className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold ring-1 ${VERDICT_STYLE[pair.verdict] || "bg-stone-100 ring-stone-200"}`}>
                              {VERDICT_LABEL[pair.verdict] || pair.verdict}
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                          status === "ai"
                            ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                            : status === "hardcoded"
                              ? "bg-stone-100 text-stone-700 ring-1 ring-stone-200"
                              : "bg-amber-50 text-amber-800 ring-1 ring-amber-200"
                        }`}
                        data-testid={`pair-status-${pair.pair_key}`}
                      >
                        {status === "ai"
                          ? "AI-generated"
                          : status === "hardcoded"
                            ? "Hardcoded"
                            : "Pending"}
                      </span>
                      {pair.in_db ? (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={busyKey === pair.pair_key || bulkBusy}
                            onClick={() => onGenerateOne(pair)}
                            data-testid={`pair-regen-${pair.pair_key}`}
                          >
                            {busyKey === pair.pair_key
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <Sparkles className="h-3.5 w-3.5" />}
                            <span className="ml-1">Re-gen</span>
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            disabled={busyKey === pair.pair_key || bulkBusy}
                            onClick={() => onRollbackOne(pair)}
                            data-testid={`pair-rollback-${pair.pair_key}`}
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          disabled={busyKey === pair.pair_key || bulkBusy}
                          onClick={() => onGenerateOne(pair)}
                          data-testid={`pair-generate-${pair.pair_key}`}
                        >
                          {busyKey === pair.pair_key
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <Sparkles className="h-3.5 w-3.5" />}
                          <span className="ml-1">Generate</span>
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>
    </main>
  );
}
